import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import type { DailyCallService } from '@services/dailycall';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { useRecordingContext } from '../../../context/RecordingContext';
import { useTelehealthCallManager } from '../context/useTelehealthCallManager';
import { useFullscreen } from '../hooks/useFullscreen';
import { clampToViewport, useResizable } from '../hooks/useResizable';
import { selectCallPanelRect, setCallPanelRect,setChatPanelOpen } from '../store';
import type { TelehealthSession } from '../types';

import { CompactPipView } from './pip/CompactPipView';
import { PipView } from './pip/PipView';

interface CallPanelProps {
  session: TelehealthSession;
  activeEncounterId: string;
  dailyCallService: DailyCallService;
  isRecording: boolean;
  isRecordingStarting?: boolean;
  isRecordingPaused?: boolean;
  onLeaveCall: () => void;
  onPauseRecording: () => void;
  onResumeRecording: () => void;
}

const DEFAULT_VW_FRACTION = 0.33;
const ASPECT_RATIO = 0.75; // 4:3
const MIN_WIDTH = 320;
const MIN_HEIGHT = 240;
const MAX_VIEWPORT_FRACTION = 0.8;
const WIDTH_BREAKPOINT = 400;
const EDGE_MARGIN = 24;

function getDefaultRect() {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.max(MIN_WIDTH, Math.round(vw * DEFAULT_VW_FRACTION));
  const height = Math.max(MIN_HEIGHT, Math.round(width * ASPECT_RATIO));
  return {
    width,
    height,
    x: vw - width - EDGE_MARGIN,
    y: vh - height - EDGE_MARGIN,
  };
}

export function CallPanel({
  session,
  activeEncounterId,
  dailyCallService,
  isRecording,
  isRecordingStarting,
  isRecordingPaused,
  onLeaveCall,
  onPauseRecording,
  onResumeRecording,
}: CallPanelProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { telehealth } = useEventTracking();
  const dispatch = useAppDispatch();
  const storedRect = useAppSelector(selectCallPanelRect);

  const { audioInputDeviceService, setSelectedMicrophone } = useRecordingContext();
  const { patientLink, pip } = useTelehealthCallManager();

  const isOnActiveEncounter = location.pathname.includes(`/encounters/${activeEncounterId}`);
  const callPanelRef = useRef<HTMLDivElement>(null);
  const fullscreen = useFullscreen(callPanelRef);


  // Compute initial rect + max dimensions on mount
  const initialRect = useMemo(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return storedRect ? clampToViewport(storedRect, vw, vh) : getDefaultRect();
    // Only compute once on mount — storedRect changes are handled by the hook's sync
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [maxWidth, setMaxWidth] = useState(() => Math.round(window.innerWidth * MAX_VIEWPORT_FRACTION));
  const [maxHeight, setMaxHeight] = useState(() => Math.round(window.innerHeight * MAX_VIEWPORT_FRACTION));

  const handleRectChange = useCallback(
    (rect: { width: number; height: number; x: number; y: number }) => {
      dispatch(setCallPanelRect(rect));
    },
    [dispatch],
  );

  const {
    width,
    height,
    x,
    y,
    isResizing,
    isDragging,
    resizeHandles,
    dragHandleProps,
    setRect: setResizableRect,
  } = useResizable({
    initialWidth: initialRect.width,
    initialHeight: initialRect.height,
    initialX: initialRect.x,
    initialY: initialRect.y,
    minWidth: MIN_WIDTH,
    minHeight: MIN_HEIGHT,
    maxWidth,
    maxHeight,
    onResizeEnd: handleRectChange,
    onMoveEnd: handleRectChange,
  });

  // Re-clamp mini player position and size when the browser window is resized
  useEffect(() => {
    let rafId: number;
    const handleViewportResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const newMaxW = Math.round(vw * MAX_VIEWPORT_FRACTION);
        const newMaxH = Math.round(vh * MAX_VIEWPORT_FRACTION);
        setMaxWidth(newMaxW);
        setMaxHeight(newMaxH);

        setResizableRect(prev => {
          const clampedW = Math.max(MIN_WIDTH, Math.min(prev.width, newMaxW));
          const clampedH = Math.max(MIN_HEIGHT, Math.min(prev.height, newMaxH));
          const clamped = clampToViewport(
            { width: clampedW, height: clampedH, x: prev.x, y: prev.y },
            vw,
            vh,
          );
          if (
            clamped.x !== prev.x ||
            clamped.y !== prev.y ||
            clampedW !== prev.width ||
            clampedH !== prev.height
          ) {
            dispatch(setCallPanelRect(clamped));
            return clamped;
          }
          return prev;
        });
      });
    };

    window.addEventListener('resize', handleViewportResize);
    return () => {
      window.removeEventListener('resize', handleViewportResize);
      cancelAnimationFrame(rafId);
    };
  }, [dispatch, setResizableRect]);

  const isCompact = width < WIDTH_BREAKPOINT;
  const compactControls = !fullscreen.isFullscreen && width < 600;

  const handleReturnToEncounter = useCallback(() => {
    navigate(`/encounters/${activeEncounterId}`);
  }, [activeEncounterId, navigate]);

  const handleOpenChat = useCallback(() => {
    dispatch(setChatPanelOpen(true));
    navigate(`/encounters/${activeEncounterId}`);
  }, [activeEncounterId, dispatch, navigate]);

  // Leave always stops recording first (handled by TelehealthCallManager.handleLeaveInternal)
  const handleLeaveClick = useCallback(() => {
    telehealth.callLeft({
      appointment_id: session.appointmentId,
      encounter_id: session.encounterId ?? '',
    });
    onLeaveCall();
  }, [onLeaveCall, session, telehealth]);

  const isInteracting = isResizing || isDragging;

  return (
    <>
      {/* Outer container — overflow visible for resize handles that extend outside */}
      <Box
        sx={{
          position: 'fixed',
          left: x,
          top: y,
          width,
          height,
          zIndex: 1299,
          transition: isInteracting
            ? 'none'
            : 'width 200ms ease, height 200ms ease, left 200ms ease, top 200ms ease',
        }}
      >
        {/* Resize handles — absolutely positioned, extend outside the box */}
        {resizeHandles}

        {/* Inner content wrapper — clips content with border radius */}
        <Box
          ref={callPanelRef}
          {...dragHandleProps}
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            cursor: dragHandleProps.style.cursor,
            position: 'relative',
          }}
        >
          {isCompact ? (
            <CompactPipView
              dailyCallService={dailyCallService}
              isRecording={isRecording}
              isRecordingStarting={isRecordingStarting}
              isRecordingPaused={isRecordingPaused}
              isOwner={session.isOwner}
              appointmentId={session.appointmentId}
              onLeave={handleLeaveClick}
              onClose={isOnActiveEncounter ? undefined : handleReturnToEncounter}
              onEnterPip={pip.toggle}
              isPipSupported={pip.isSupported}
              onOpenChat={handleOpenChat}
              onPauseRecording={onPauseRecording}
              onResumeRecording={onResumeRecording}
              onToggleFullscreen={fullscreen.isSupported ? fullscreen.toggle : undefined}
              isFullscreen={fullscreen.isFullscreen}
            />
          ) : (
            <PipView
              dailyCallService={dailyCallService}
              audioInputDeviceService={audioInputDeviceService}
              setSelectedMicrophone={setSelectedMicrophone}
              isRecording={isRecording}
              isRecordingStarting={isRecordingStarting}
              isRecordingPaused={isRecordingPaused}
              session={session}
              onLeave={handleLeaveClick}
              onClose={isOnActiveEncounter ? undefined : handleReturnToEncounter}
              onEnterPip={pip.toggle}
              isPipSupported={pip.isSupported}
              onPauseRecording={onPauseRecording}
              onResumeRecording={onResumeRecording}
              onToggleFullscreen={fullscreen.isSupported ? fullscreen.toggle : undefined}
              isFullscreen={fullscreen.isFullscreen}
              compactControls={compactControls}
              popoverContainerEl={fullscreen.isFullscreen ? callPanelRef.current : null}
            />
          )}
        </Box>
      </Box>
    </>
  );
}
